import { useEffect, useMemo, useState } from 'react';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { PersonalNikkeCard } from '../../personal-nikke/shared/PersonalNikkeCard.jsx';
import { RaidPracticeDeckDisplay } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import { loadUnionMemberDetail } from '../api/unionMemberDetailApi.js';

function formatEok(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
}

export function UnionMemberDetailDialog({ member, onClose, mobile = false }) {
  const [tab, setTab] = useState('nikke');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    loadUnionMemberDetail(member.memberOpenid)
      .then((value) => { if (active) setDetail(value); })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : '멤버 상세정보를 불러오지 못했습니다.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [member.memberOpenid]);

  const practiceGroups = useMemo(() => {
    const groups = new Map();
    for (const record of detail?.practiceRecords || []) {
      const seasonNo = Number(record.seasonNo || 0);
      if (!groups.has(seasonNo)) groups.set(seasonNo, []);
      groups.get(seasonNo).push(record);
    }
    return [...groups.entries()].sort((a, b) => b[0] - a[0]);
  }, [detail?.practiceRecords]);

  return (
    <div className={`union-member-detail-backdrop ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="union-member-detail-dialog" role="dialog" aria-modal="true" aria-label={`${member.nickname} 상세정보`}>
        <header className="union-member-detail-head">
          <div><span>MEMBER DETAIL</span><h2>{member.nickname}</h2><p>니케·오버로드와 모의전 상세정보</p></div>
          <button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button>
        </header>
        <div className="union-member-detail-tabs" role="tablist">
          <button type="button" className={tab === 'nikke' ? 'active' : ''} onClick={() => setTab('nikke')}>니케·오버로드</button>
          <button type="button" className={tab === 'practice' ? 'active' : ''} onClick={() => setTab('practice')}>모의전</button>
        </div>
        <div className="union-member-detail-body">
          {loading ? <div className="union-member-detail-state"><span /><p>상세정보를 불러오는 중…</p></div> : null}
          {error ? <div className="union-member-detail-state error"><strong>불러오지 못했습니다.</strong><p>{error}</p></div> : null}
          {!loading && !error && tab === 'nikke' ? (
            <>
              {detail?.profile ? <section className="union-member-detail-profile"><div><span>최근 동기화</span><strong>{formatDate(detail.profile.syncedAt)}</strong></div><div><span>지휘관 레벨</span><strong>{Number(detail.profile.playerLevel || 0).toLocaleString('ko-KR')}</strong></div><div><span>대표 전투력</span><strong>{Number(detail.profile.teamCombat || 0).toLocaleString('ko-KR')}</strong></div><div><span>보유 니케</span><strong>{Number(detail.profile.ownedCount || 0).toLocaleString('ko-KR')}명</strong></div></section> : null}
              {detail?.characters?.length ? <div className="personal-nikke-grid union-member-detail-nikke-grid">{detail.characters.map((entry) => <PersonalNikkeCard personal={entry} mobile={mobile} key={entry.nameCode} />)}</div> : <div className="union-member-detail-empty">{detail?.personalError ? <><strong>니케 정보를 불러오지 못했습니다.</strong><p>{detail.personalError}</p></> : Number(detail?.syncedCharacterCount || 0) > 0 ? <><strong>오버로드를 장착한 니케가 없습니다.</strong><p>보유 니케 {Number(detail.syncedCharacterCount).toLocaleString('ko-KR')}명은 동기화되어 있고, 이 목록은 오버로드가 있는 니케만 보여줍니다.</p></> : <><strong>동기화된 니케 정보가 없습니다.</strong><p>해당 멤버가 내 정보에서 니케 정보를 동기화해야 표시됩니다.</p></>}</div>}
            </>
          ) : null}
          {!loading && !error && tab === 'practice' ? (
            practiceGroups.length ? <div className="union-member-practice-groups">{practiceGroups.map(([seasonNo, records]) => <section key={seasonNo}><header><h3>{seasonNo}차</h3><span>{records.length}개 슬롯</span></header><div>{records.map((record) => <article key={record.id}><header><div><strong>{record.title || `슬롯 ${record.slotNo}`}</strong><small>보스 {record.bossNo} · {record.phaseNo || '-'}단 · 슬롯 {record.slotNo}</small></div><b>{formatEok(record.damageEok)}</b></header><RaidPracticeDeckDisplay deck={record.deck} mobile={mobile} /><footer><span>{record.used ? '공격완료' : '사용 가능'}</span><p>{record.note || '메모 없음'}</p></footer></article>)}</div></section>)}</div> : <div className="union-member-detail-empty"><strong>저장된 모의전이 없습니다.</strong><p>해당 멤버의 모의전 슬롯이 저장되면 표시됩니다.</p></div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
